import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Jersey } from '@sorare/core/src/assets/icons/Jersey';
import { Bench } from '@sorare/core/src/atoms/icons/Bench';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { DropRank } from '@sorare/core/src/components/commonGame/DropRank';

import type { PackSlotOdds_cardPackSlot } from './__generated__/index.graphql';

const OddRow = styled(SBHorizontal)`
  background: var(--c-nd-50);
  padding: 0 var(--double-unit);
  border-radius: var(--double-unit);
  height: 52px;
`;
const StyledJerseyIcon = styled(Jersey)`
  color: var(--c-onBase-success);
`;

const StyledCardImg = styled(CardImg)`
  width: 22px;
  &:hover {
    transform: scale(1.3);
  }
  transition: transform 0.2s ease-in-out;
`;

type Props = {
  cardPackSlot: PackSlotOdds_cardPackSlot;
};

export const PackSlotOdds = ({ cardPackSlot }: Props) => {
  const { playingStatusOdds, rankOdds } = cardPackSlot;

  if (playingStatusOdds.length) {
    return (
      <Vertical>
        {playingStatusOdds.map(odd => (
          <OddRow key={`${odd.starter}-${odd.probabilityBasisPoint}`}>
            {odd.starter ? (
              <Horizontal>
                <StyledJerseyIcon />
                <LabelL bold>
                  <FormattedMessage
                    id="PackSlotOdds.starter"
                    defaultMessage="Starter"
                  />
                </LabelL>
              </Horizontal>
            ) : (
              <Horizontal>
                <Bench color="var(--c-nd-600)" />
                <LabelL bold>
                  <FormattedMessage
                    id="PackSlotOdds.nonStarter"
                    defaultMessage="Non-Starter"
                  />
                </LabelL>
              </Horizontal>
            )}
            <LabelL color="var(--c-nd-700)">
              <FormattedNumber
                style="percent"
                value={odd.probabilityBasisPoint / 10000}
                maximumFractionDigits={2}
              />
            </LabelL>
          </OddRow>
        ))}
      </Vertical>
    );
  }

  return (
    <Vertical>
      {rankOdds.map(odd => (
        <OddRow key={odd.dropRank}>
          <Horizontal center>
            {!!odd.commonPlayer && (
              <>
                <StyledCardImg src={odd.commonPlayer.pictureUrl} width={80} />
                <LabelL bold>{odd.commonPlayer.anyPlayer.displayName}</LabelL>
              </>
            )}
            <DropRank dropRank={odd.dropRank} />
          </Horizontal>
          <LabelL color="var(--c-nd-700)">
            <FormattedNumber
              style="percent"
              value={odd.probabilityBasisPoint / 10000}
              maximumFractionDigits={2}
            />
          </LabelL>
        </OddRow>
      ))}
    </Vertical>
  );
};

PackSlotOdds.fragments = {
  cardPackSlot: gql`
    fragment PackSlotOdds_cardPackSlot on CardPackSlot {
      playingStatusOdds {
        probabilityBasisPoint
        starter
      }
      rankOdds {
        probabilityBasisPoint
        dropRank
        commonPlayer {
          id
          pictureUrl
          anyPlayer {
            slug
            displayName
          }
        }
      }
    }
  ` as TypedDocumentNode<PackSlotOdds_cardPackSlot>,
};
